import { AxiosError } from "axios";
import { RouteComponentProps } from "react-router-dom";
import { SelectGridProps } from "../components/select-grid";
import { SelectMultiProps } from "../components/select-multi";
import { VeiculoData } from "../components/grafico-teia";

export interface ApiError {
    title?: string;
    errors?: Record<string, string[]> | string[];
    Mensagem?: string;
    mensagem?: string;
    message?: string;
}

export interface Veiculo {
    id?: string;
    ano: string;
    cor?: string;
    marca: string;
    placa: string;
    modelo: string;
    imagem?: string;
    dataCompra?: Date;
    hodometro?: number;
    idEmpresa: string;
    tipoVeiculo: number;
    custo?: number;
    vencimentoLicenciamento?: Date;
    status?: string;
    vendido?: boolean;
    inativo?: boolean;
    renavam?: string;
    chassi?: string;
    roubado?: boolean;
    idFilial?: string;
    observacaoInativacao?: string;
    bloqueioRemotoHabilitado?: boolean;
}

export interface Locatario {
    id?: string;
    cep?: string,
    nome: string;
    email?: string,
    numero?: string,
    estado?: string,
    cidade?: string,
    bairro?: string,
    status?: string,
    telefone?: string,
    documento: string;
    idEmpresa: string;
    dataCadastro: Date,
    logradouro?: string,
    complemento?: string,
    kmMedioPorDia?: number,
    telefoneReserva?: string,
    numeroRegistroCnh?: string;
    vencimentoHabilitacao?: Date,
    tipoDocumento?: number;
    idFilial?: string;
    observacaoInativacao?: string;
    cartaoNomeTitular?: string;
    cartaoDocumentoTitular?: string;
    cartaoUltimos4Digitos?: string;
    cartaoBandeira?: string;
    cartaoValidade?: string;
    usaApp?: boolean;
    ultimoLoginApp?: string;
}

export interface UsuarioLocatario {
    id?: string;
    nome: string;
    email: string;
    senha: string;
    idEmpresa: string;
    idLocatario: string;
}

export interface PrestadorServico {
    id?: string;
    cep: string,
    nome: string;
    email: string,
    numero: string,
    estado: string,
    cidade: string,
    bairro: string,
    status?: string,
    telefone: string,
    documento: string;
    idEmpresa: string;
    logradouro: string,
    complemento: string,
    tipoDocumento: number,
    idFilial?: string;
}

export interface ModeloContrato {
    id?: string;
    titulo: string;
    descricao: string;
    documento: string;
}

export interface Servico {
    id?: string;
    nome: string;
    valor: number;
    periodoEmKm: number,
    idEmpresa: string;
    idFilial?: string;
}

export interface ServicoVeiculo {
    id?: string;
    valor: number;
    idEmpresa: string;
    idVeiculo: string;
    hodometro: number;
    descricao: string;
    dataCadastro: Date;
    dataRealizado: Date;
    idPrestadorServico: string;
    servicoVeiculoPecas: ServicoVeiculoPeca[];
    valorMaoDeObra?: number;
    valorPecas?: number;
    servicoVeiculoServicos: ServicoVeiculoServico[];
    idBanco?: string | null;
    bancosDivergentes?: boolean;
}

export enum BillingTypeAsaas {
    UNDEFINED = 0,
    BOLETO = 1,
    CREDIT_CARD = 2,
    PIX = 3,
}

export interface Locacao {
    id?: string;
    veiculo?: any
    locatario?: any;
    dataInicio: Date;
    idVeiculo: string;
    idEmpresa: string;
    kmInicial: number;
    dataTermino?: Date;
    idLocatario: string;
    valorCaucao: number;
    valorAluguel: number;
    status: StatusLocacao;
    diaPagamento: DiaDaSemana;
    tempoContratoEmMeses: number;
    periodicidadePagamento: TipoPeriodicidadePagamento;
    gerarLancamentoCaucaoComoPago: boolean;
    gerarPagamentoInicialComoPago: boolean;
    definirPagamentosAnteriorAHojeComoPago: boolean;
    tipoPeriodicidadeTempoContrato: TipoPeriodicidadePagamento;
    desejoGerarPagamentoUnico: boolean;
    dataPagamento?: Date;
    kmFinal?: number;
    dataSegundoPagamentoQuinzena?: Date;
    ajusteDiaInexistente?: number;
    locacaoEmAndamento?: boolean;
    observacao?: string;
    vendeuVeiculo?: boolean;
    documento?: any;
    fiador?: any;
    asaasSubscriptionId?: string;
    asaasControlaJurosEMulta?: boolean;
    /**
     * Escolha do locador de NÃO enviar esta locação ao Asaas, mesmo com a conta integrada.
     * undefined/true = envia. Definida na criação; vira somente leitura assim que existe assinatura.
     */
    integrarAoAsaas?: boolean;
    /** Forma de pagamento da caução. undefined/null = herda a do aluguel. */
    caucaoTipoDePagamento?: BillingTypeAsaas | null;
    /** Cobrar a caução no Asaas. undefined/null = segue a decisão da locação. */
    cobrarCaucaoNoAsaas?: boolean | null;
    valorJurosAoDia?: number;
    jurosAoMes?: number;
    multaFixa?: number;
    multaPercentual?: number;
    asaasTipoDePagamento?: BillingTypeAsaas;
    opcaoDeCompra?: boolean;
    autentiqueDocumentId?: string;
    documentoContrato?: string;
    empresa?: Empresa;
    qtdPagamentosEmAtraso?: number;
    valorTotalEmAtraso?: number;
    dataUltimoPagamentoPrevisto?: string;
    idBanco?: string | null;
    /** Descrição customizada do aluguel recorrente no Asaas. Opcional; fallback "ALUGUEL - <placa>". */
    descricaoCustomAluguel?: string | null;
    limiteQuilometragem?: number | null;
    idFilial?: string;
    comissaoFilialTipoCalculo?: number;
    comissaoFilialValor?: number | null;
}

/**
 * Documento vinculado a uma locação. Pode ser do tipo "contrato" (gerado pelo sistema)
 * ou "documento_extra" (enviado pelo usuário; limite de 3 por locação).
 */
export interface DocumentoLocacao {
    id: string;
    tipoEntidade: string;
    entidadeId: string;
    nomeOriginal: string;
    nomeSalvo: string;
    tipo: 'contrato' | 'documento_extra' | string;
    mimeType: string;
    criadoEm: string;
    urlAcesso?: string;
    idEmpresa?: string;
    descricao?: string | null;
    tamanho?: number | null;
    autentiqueDocumentId?: string | null;
    assinadoEm?: string | null;
}

export enum DiaDaSemana {
    sabado = 6,
    nenhum = 0,
    domingo = 7,
    tercaFeira = 2,
    sextaFeira = 5,
    quartaFeira = 3,
    quintaFeira = 4,
    segundaFeira = 1,
}

export enum StatusLocacao {
    Ativa = 1,
    Nenhum = 0,
    Inativa = 2,
    Encerrada = 3,
}

export interface ServicoVeiculoServico {
    idServico: string;
}

export interface ServicoVeiculoPeca {
    idPeca: string;
    provenienteDoEstoque?: string;
    quantidadeUsada?: number;
    valor?: number;
}

export interface Peca {
    id?: string;
    nome: string;
    marca: string,
    modelo: string;
    valor?: number;
    qtdEstoque?: number;
    idEmpresa: string;
    tipoVeiculo: number;
    vidaUtilEmKm: number;
    provenienteDoEstoque?: string;
    idFilial?: string;
}

export interface Categoria {
    id?: string;
    cor: string,
    nome: string;
    descricao: string;
    idEmpresa: string;
    ehPadrao?: boolean;
    /** Identidade de categoria de sistema renomeável ('CAUCAO'). */
    chave?: string | null;
}

export interface Estoque {
    id?: string;
    idPeca: string;
    valor?: number;
    idEmpresa: string;
    quantidade: number;
    gerarLancamentoDoEstoque: boolean;
    idPrestadorServico?: string;
    prestadorServico?: { id: string; nome: string };
    parcelas?: number;
    dataVencimento?: string;
    periodicidade?: number;
    idCategoriaAdicional?: string;
    identificador?: string;
    idFilial?: string;
}

export enum StatusPlanoManutencao {
    EmDia = 0,
    Proximo = 1,
    Vencido = 2,
    Concluido = 3
}

export interface PlanoManutencao {
    id?: string;
    idEmpresa: string;
    idVeiculo: string;
    veiculo?: Veiculo;
    idPeca?: string;
    peca?: Peca;
    idServico?: string;
    servico?: { id: string; nome: string };
    intervaloKm: number;
    hodometroUltimaExecucao: number;
    dataUltimaExecucao?: Date;
    dataCriacao?: Date;
    recorrente: boolean;
    concluido: boolean;
    observacao?: string;
    idServicoVeiculoOrigem?: string;
    hodometroAtual: number;
    kmRestantes: number;
    status: number;
    nomeLocatario?: string;
    telefoneLocatario?: string;
    idFilial?: string;
}

export interface TrocaDeOleo {
    id?: string;
    data: Date;
    hodometro: number;
    idEmpresa: string;
    idLocacao: string;
    obs: string;
    status: number;
    idFilial?: string;
}

export interface Vistoria {
    id?: string;
    data: Date;
    hodometro: number;
    idEmpresa: string;
    idLocacao: string;
    obs: string;
    status: number;
    idFilial?: string;
}

export interface Lancamento {
    id?: string;
    valor: number;
    descricao: string;
    dataCriacao: Date;
    idEmpresa: string;
    dataInicio?: Date;
    dataRealizado: Date;
    DataPagamentoFoiFeito?: Date;
    tipo: TipoLancamento;
    idLocacao?: string | null;
    idVeiculo?: string | null;
    parcelasPagamento?: number;
    status?: TipoStatusLancamento;
    idLancamentoOriginal?: string;
    ehLancamentoOriginal?: boolean;
    alterarLancamentosPosteriorAData?: boolean;
    propagarParaParcelas?: boolean;
    lancamentoCategorias?: LancamentoCategoria[];
    periodicidadePagamento?: TipoPeriodicidadePagamento;
    /** Juros ao mês (%) e multa da cobrança no Asaas. O Asaas calcula; o sistema só repassa. */
    jurosAoMes?: number | null;
    multaFixa?: number | null;
    multaPercentual?: number | null;
    /** Valor antes dos juros/multa que o Asaas cobrou no recebimento. */
    valorOriginal?: number | null;
    asaasPaymentId?: string;
    idBanco?: string | null;
    idFilial?: string;
    idManutencao?: string | null;
}

export interface Multa {
    id?: string;
    idLocacao?: string | null;
    valor: number;
    status?: TipoStatusMulta | TipoStatusMulta.Nenhum;
    DescricaoInfracao: string;
    AutoInfracao?: string | null;
    CodigoInfracao?: string | null;
    NumeroRenainf?: string | null;
    DataHoraCometimento: Date;
    DataHoraVencimento?: Date | null;
    RealInfratorIndicado: boolean;
    idEmpresa: string;
    idVeiculo: string | null;
    naoGerarLancamentoFinanceiro: boolean;
    idBanco?: string | null;
    bancosDivergentes?: boolean;
    idFilial?: string | null;
}

export interface MultaLancameto {
    id?: string;
    idLancamento?: string | null;
    idEmpresa: string | null;
    idMulta: string;
    valor: number;
    descricaoInfracao: string;
    idLocacao: string;
    idVeiculo: string;
    periodicidadePagamento: TipoPeriodicidadePagamento;
    parcelas: number;
    dataInicio: Date;
    desconto: number;
    definirMultaComoPaga: boolean;
    quitacaoAutomatica?: boolean;
}

export interface Pagamento {
    id?: string;
    idLocacao: string;
    valor: number;
    descricao: string;
    dataPrevista: Date;
    dataUltimoPagamento?: Date;
    dataPagamentoRealizado?: string;
    status: StatusPagamento;
    ehUltimoPagamento: boolean;
    idEmpresa: string;
    asaasPaymentId?: string;
    gerarApenasNoAsaas?: boolean;
    naoEditarNoAsaas?: boolean;
    naoContabilizarJuros?: boolean;
    naoContabilizarMultas?: boolean;
    idBanco?: string | null;
    temDivergenciaAsaas?: boolean;
    idFilial?: string;
}

export interface ContextoLancamento {
    dataInicial?: Date | null;
    dataFinal?: Date | null;
    idEmpresa?: string;
    tipo?: TipoLancamento;
    status?: TipoStatusLancamento;
    trazerArquivos?: boolean;
    tipoPeriodo?: string;
}

export interface ContextoMulta {
    dataInicial?: Date | null;
    dataFinal?: Date | null;
    idEmpresa?: string;
    status?: TipoStatusMulta;
    tipoPeriodo?: string;
    idVeiculo?: string;
    idLocatario?: string;
}

export interface ContextoRelatorioServicoVeiculo {
    dataFinal: Date;
    dataInicial: Date;
    idVeiculo?: string;
    idEmpresa?: string;
    valorFinal?: number;
    valorInicial?: number;
    idPrestadorDeServico?: string;
    ordenacao?: TipoOrdenacaoRelatorio;
    idFiliais?: string[];
}

export interface ContextoRelatorioLocacoes {
    dataFinal?: Date;
    dataInicial?: Date;
    vigenteEm?: Date;
    idVeiculo?: string;
    status?: StatusLocacao;
    ordenacao?: TipoOrdenacaoRelatorio;
    opcaoDeCompra?: boolean;
    idFiliais?: string[];
}

export interface ContextoRelatorioVeiculos {
    dataFinal: Date;
    dataInicial: Date;
    ativo?: boolean;
    ordenacao?: TipoOrdenacaoRelatorio;
    finalPlaca?: string;
    idEmpresa: string;
    idFiliais?: string[];
}

export interface ContextoRelatorioDespesas {
    dataFinal: Date;
    dataInicial: Date;
    idVeiculo?: string;
    idLocacao?: string;
    idLocatario?: string;
    idEmpresa: string;
    trazerSaidasPendentes: boolean;
    ordenacao?: TipoOrdenacaoRelatorio;
    filtrarPorData?: TipoFiltroData;
    idFiliais?: string[];
    idBanco?: string;
    filtrarApenasSemBanco?: boolean;
    exibirInformacoesBancarias?: boolean;
}

export interface ContextoRelatorioReceitas {
    dataFinal: Date;
    dataInicial: Date;
    idVeiculo?: string;
    idLocacao?: string;
    idLocatario?: string;
    trazerSaidasPendentes: boolean;
    trazerPagamentosPendentes: boolean;
    exibirApenasPendentes?: boolean;
    exibir?: TipoExibirRelatorioReceita;
    ordenacao?: TipoOrdenacaoRelatorio;
    filtrarPorData?: TipoFiltroData;
    idFiliais?: string[];
    idBanco?: string;
    filtrarApenasSemBanco?: boolean;
    exibirInformacoesBancarias?: boolean;
}

export interface ContextoRelatorioFluxoCaixa {
    dataFinal: Date;
    dataInicial: Date;
    idVeiculo?: string;
    idLocacao?: string;
    idLocatario?: string;
    ordenacao?: TipoOrdenacaoRelatorio;
    separarEntradasSaidas: boolean;
    filtrarPorData?: TipoFiltroData;
    idFiliais?: string[];
    idBanco?: string;
    filtrarApenasSemBanco?: boolean;
    exibirInformacoesBancarias?: boolean;
}

export interface RelatorioServicoVeiculo {
    data: string;
    veiculo: Veiculo;
    valor: number;
    valorTotalPeca: number;
    valorTotalServicos: number;
    descricao: string;
    hodometro: number;
    prestadorDeServico: PrestadorServico;
    servicos?: Servico[];
    pecas?: Peca[];
}

export interface RelatorioDespesas {
    data: string;
    veiculo: Veiculo;
    valor: number;
    valorNumerico?: number;
    motivo: string;
    locatario: Locatario;
    status: string;
    categoria: Categoria;
    bancoFormatado?: string;
}

export interface RelatorioReceitas {
    data: string;
    veiculo: Veiculo;
    valor: number;
    valorNumerico?: number;
    motivo: string;
    locatario: Locatario;
    status: string;
    ehPagamento: boolean;
    categoria: Categoria;
    bancoFormatado?: string;
}

export interface RelatorioFluxoCaixa {
    data: string;
    tipo: string;
    valor: number;
    valorNumerico?: number;
    motivo: string;
    status: string;
    veiculo: Veiculo;
    locatario: Locatario;
    categoria: Categoria;
    bancoFormatado?: string;
}


export interface RelatorioLocacoes {
    status: string;
    veiculo: Veiculo;
    kmInicial: number;
    dataInicio: string;
    dataTermino: string;
    valorCaucao: number;
    locatario: Locatario;
    valorAluguel: number;
    tempoContratoEmMeses: number;
    periodicidadePagamento: string;
    substituidoNaData?: boolean;
    placaAtual?: string;
    observacao?: string;
}

export interface RelatorioVeiculos {
    placa: string;
    marca: string;
    modelo: string;
    ano: string;
    cor: string;
    investimento: number;
    dataCompra: Date;
    situacao: string;
}

export interface FormLogin {
    email: string;
    senha: string;
}

export interface LoginSistema {
    email: string;
    senha: string;
}

export interface FormCadastrar {
    email: string;
    senha: string;
    confirmaSenha: string;
    nomeEmpresa: string;
    nomeUsuario: string;
    documento: string;
    quantidadeVeiculos: number;
}

export interface CadastrarSistema {
    email: string;
    senha: string;
    nomeEmpresa: string;
    nomeUsuario: string;
    documento: string;
    telefone: string;
    quantidadeVeiculos: number;
    codigoAfiliado?: string;
}

export interface AcaoGrid {
    Texto: string;
    OnClick?: (entidade: any) => void;
    Icone?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    Imagem?: string;
    Cor?: string;
    Tamanho?: 'small' | 'medium' | 'large';
    SubMenu?: AcaoGrid[];
    Condicao?: (entidade: any) => boolean;
    TooltipDisabled?: string | ((entidade: any) => string);
}

interface Acesso {
    acao: '' | 'acessobloqueado';
}

interface Listagem {
    pagina: string;
}

export interface Opcao {
    valor: string | number | boolean;
    label: string;
    color?: string;
}

export interface AjudinhaProps {
    texto: string;
    ehDestacado?: boolean;
    autoCloseTime?: number;
}

export interface Empresa {
    id?: string;
    nome: string;
    documento: string;
    expiraEm: Date;
    limiteDeVeiculos: number;
    ativa: boolean;
    cep?: string;
    rua?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    complemento?: string;
    numero?: string;
    logo?: string;
    tipoPlano?: string;
    emissaoNotaFiscalLiberada?: boolean;
    limiteNotasFiscais?: number;
    integradoLocaSmartCheck?: boolean;
    locaSmartCheckTenantId?: string;
}

export interface EmpresaAdmin {
    id: string;
    nome: string;
    documento: string;
    expiraEm: string;
    limiteDeVeiculos: number;
    ativa: boolean;
    planoGratuito: boolean;
    emPeriodoDeTeste: boolean;
    tipoPlano: string;
    dataDeCadastro: string;
    limiteDeUsuarios: number;
    qtdUsuarios: number;
    qtdVeiculos: number;
    emailAdmin: string;
    integradoLocaSmartCheck: boolean;
    emissaoNotaFiscalLiberada: boolean;
    limiteNotasFiscais: number;
    treinamentoStatus?: string | null;
    treinamentoRespondidoEm?: string | null;
    treinamentoSolicitadoPor?: string | null;
    locaSmartTrack?: boolean;
    quantidadeVeiculosRastreados?: number;
}

export interface ConfiguracaoFilialType {
    id?: string;
    idFilial: string;
    chaveAPIAsaas?: string;
    chavePix?: string;
    mensagemWhatsAppCobranca?: string;
    mensagemWhatsAppManutencao?: string;
    autentiqueApiKey?: string;
    autentiqueRemetenteEmail?: string;
    azulMonitorEmail?: string;
    azulMonitorConfigurado?: boolean;
    certificadoDigitalArquivo?: string;
    certificadoDigitalSenha?: string;
    ambiente?: number;
    codigoMunicipio?: string;
    inscricaoMunicipal?: string;
    padraoNacional?: boolean;
    usarServicoPadraoNFSe?: boolean;
    regimeEspecialTributacao?: number;
    proximoNumeroLote?: number;
    proximoNumeroRps?: number;
    serieRps?: string;
    naturezaOperacao?: number;
    tipoTributacaoServico?: number;
    wsChaveAutorizacao?: string;
}

export interface ConfiguracaoEmpresa {
    id?: string;
    idEmpresa?: string;
    empresa?: Empresa;
    mensagemWhatsAppCobranca: string;
    mensagemWhatsAppManutencao?: string;
    chavePix: string;
    chaveAPIAsaas: string;
    autentiqueApiKey: string;
    autentiqueRemetenteEmail?: string;
    // O servidor não devolve as chaves; só informa se estão configuradas e os 4 últimos caracteres.
    chaveAPIAsaasConfigurada?: boolean;
    chaveAPIAsaasFinal?: string | null;
    autentiqueApiKeyConfigurada?: boolean;
    autentiqueApiKeyFinal?: string | null;

    // Agillock (rastreamento) — senha nunca retornada pelo backend
    azulMonitorEmail?: string;
    azulMonitorConfigurado?: boolean;

    // NFSe
    certificadoDigitalArquivo?: string;
    certificadoDigitalSenha?: string;
    ambiente?: number;
    codigoMunicipio?: string;
    inscricaoMunicipal?: string;
    padraoNacional?: boolean;
    usarServicoPadraoNFSe?: boolean;
    regimeEspecialTributacao?: number;
    proximoNumeroLote?: number;
    proximoNumeroRps?: number;
    serieRps?: string;
    naturezaOperacao?: number;
    tipoTributacaoServico?: number;
    wsChaveAutorizacao?: string;

    // Comissionamento
    comissaoApurarSomenteSePago?: boolean;
}

export interface ServicoNFSe {
    id?: string;
    idEmpresa: string;
    codigo: string;
    codigoCnae: string;
    codigoTributacaoMunicipio: string;
    codigoTributacaoNacional?: string;
    descricao: string;
    aliquotaIss: number;
    padrao: boolean;
    codigoNbs?: string;
    indOp?: string;
    cClassTrib?: string;
    responsavelRetencao?: number;
    tipoRetencaoIss?: number;
    cst?: string;
    aliquotaIbsEstadual?: number;
    aliquotaIbsMunicipal?: number;
    aliquotaCbs?: number;
    desconto?: number;
    descontoIncondicionado?: number;
}

export interface Cupom {
    id?: string;
    codigo: string;
    limiteDeUso?: number;
    TipoDeDesconto: number;
    PercentualDeDesconto?: number;
    ValorFixo?: number;
    ExpiraEm?: Date;
    Ativo: boolean;
    ApenasParaPeriodoDeTestes: boolean;
    UsoUnicoPorEmpresa: boolean;
}

export interface Usuario {
    id?: string;
    nome: string;
    email: string;
    senha: string;
    idEmpresa: string;
    administrador: boolean;
    permissoes?: Permissao[];
    idExpo?: string;
}

export interface BotaoExtra {
    icone: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    tooltip: string;
    link: string;
}

export interface Campo {
    linha: number
    nome: string;
    label: string;
    coluna: number;
    largura: number;
    visible?: boolean | ((values: any) => boolean);
    opcoes?: Opcao[];
    mascara?: string;
    required: boolean;
    carregando?: boolean;
    tudoMaiusculo?: boolean;
    apenasLeitura?: boolean;
    ajudinha?: AjudinhaProps;
    maximoCaracteres?: number;
    minimoCaracteres?: number;
    onCampoSair?: (e: any) => void;
    onCampoEntrar?: (e: any) => void;
    onCampoDigitar?: (e: any) => void;
    selectGridProps?: SelectGridProps;
    selectMultiProps?: SelectMultiProps;
    selectBoxIsCleareable?: boolean;
    selectBoxdefaultOption?: string;
    selectBoxHint?: string;
    botaoExtra?: BotaoExtra;
    defaultValueToggle?: string;
    valorPadrao?: any;
    allowNotFoundValues?: boolean;
    tipo: 'text' | 'decimal' | 'image' | 'money' | 'label' | 'select' | 'datetime-local' | 'date' | 'email' | 'selectgrid' | 'memo' | 'number' | 'senha' | 'checkbox' | 'percentual' | 'color' | 'file' | 'toggle' | 'multiselect' | 'selectwithnumber' | 'aviso' | 'photo' | 'video' | 'custom';
    component?: (props: { value: any, onChange: (val: any) => void, campo: Campo, entidade?: any, handleInputChange?: (campo: string, valor: string | number | boolean) => void }) => React.ReactNode;
    /** Props arbitrárias passadas ao componente custom (para casos onde value/onChange/entidade não cobrem). */
    extraProps?: Record<string, any>;
    alertType?: 'info' | 'success' | 'warning' | 'danger';
    alertIcon?: React.ReactNode;
    alertDismissible?: boolean;
    alertTitle?: string;
    alertMessage?: string;
    novo?: boolean;
    tutorialLink?: string;
    subLabel?: string;
}

export interface LancamentoCategoria {
    id: string;
    idCategoria: string;
    idLancamento: string;
    categoria?: Categoria;
    lancamento?: Lancamento;
}

export interface ModalAtualizacaoEntidade {
    forcarAtualizacao: boolean | number;
    entidadeUsadaParaAtualizacao: any;
}

export const CoresVeiculo: Opcao[] = [
    { valor: 'Prata', label: 'Prata' },
    { valor: 'Preto', label: 'Preto' },
    { valor: 'Branco', label: 'Branco' },
    { valor: 'Cinza', label: 'Cinza' },
    { valor: 'Vermelho', label: 'Vermelho' },
    { valor: 'Azul', label: 'Azul' },
    { valor: 'Verde', label: 'Verde' },
    { valor: 'Amarelo', label: 'Amarelo' },
    { valor: 'Marrom', label: 'Marrom' },
    { valor: 'Bege', label: 'Bege' },
    { valor: 'Laranja', label: 'Laranja' },
    { valor: 'Dourado', label: 'Dourado' },
    { valor: 'Roxo', label: 'Roxo' },
    { valor: 'Rosa', label: 'Rosa' },
    { valor: 'Fantasia', label: 'Fantasia' }
];

export const BillingTypeOptions: Opcao[] = [
    { valor: BillingTypeAsaas.UNDEFINED, label: 'Locatário quem escolhe' },
    { valor: BillingTypeAsaas.BOLETO, label: 'Boleto' },
    { valor: BillingTypeAsaas.CREDIT_CARD, label: 'Cartão de Crédito' },
    { valor: BillingTypeAsaas.PIX, label: 'Pix' }
];

/**
 * Mesmas formas, com a opção de não escolher nada e herdar a do aluguel. Usada pela caução e pelos
 * lançamentos, onde o caso comum é justamente seguir o aluguel sem preencher mais um campo.
 * O valor vazio é enviado como null e o backend resolve a herança.
 */
export const BillingTypeOptionsComHeranca: Opcao[] = [
    { valor: '', label: 'Mesma do aluguel' },
    ...BillingTypeOptions
];

export enum TipoPeriodicidadePagamento {
    Nenhum = 0,
    Semanal = 1,
    Quinzenal = 2,
    Mensal = 3,
    Diario = 4,
}

export enum TipoExibirRelatorioReceita {
    ApenasRecebimentos = 1,
    ApenasLançamentos = 2,
    Todos = 3,
}

export enum TipoOrdenacaoRelatorio {
    MaisRecenteParaMaisAntigo = 1,
    MaisAntigoParaMaisRecente = 2,
    Todos = 3,
}

export enum TipoFiltroData {
    DataVencimento = 1,
    DataPagamento = 2,
}

export enum TipoModoVisualizacaoRelatorio {
    Compacto = 1,
    Detalhado = 2,
}

export enum TipoLancamento {
    Nenhum = 0,
    Entrada = 1,
    Saida = 2,
    Todos = 3,
}

export enum TipoStatusLancamento {
    Nenhum = 0,
    Resolvido = 1,
    Pendente = 2,
    Todos = 3
}

export enum TipoStatusMulta {
    Nenhum = 0,
    Paga = 1,
    Vencida = 2,
    EmAberto = 3,
    Cancelada = 4,
    Todos = 5,
}

export enum StatusPagamento {
    Todos = -1,
    Pago = 1,
    Pendente = 2,
    EmAtraso = 3
}

export enum StatusTrocaDeOleo {
    Aprovada = 1,
    Recusada = 2,
    EmAnalise = 3,
    FeitoPeloLocador = 4
}

export enum StatusVistoria {
    Aprovada = 1,
    Recusada = 2,
    EmAnalise = 3,
    FeitoPeloLocador = 4
}

export enum StatusOrdemDeServico {
    Nenhum = 0,
    Aberta = 1,
    Convertida = 2,
    Cancelada = 3,
}

export interface OrdemDeServico {
    id: string;
    idVeiculo: string;
    veiculo?: any;
    hodometro: number;
    dataCadastro: Date;
    dataRealizado?: Date;
    descricao: string;
    status: StatusOrdemDeServico;
    idServicoVeiculoGerado?: string;
    idEmpresa: string;
    idFilial?: string;
    idPrestadorServico: string;
    prestadorServico?: any;
    ordemDeServicoServicos?: any[];
    ordemDeServicoPecas?: any[];
}

export enum FiltroDataPagamento {
    Hoje = 1,
    Semana = 2,
    Quinzena = 3,
    Mes = 4,
    Personalizado = 5,
    Todos = 6,
}

export const OpcoesPeriodicidadePagamento: Opcao[] = [
    { valor: TipoPeriodicidadePagamento.Diario, label: 'Dia' },
    { valor: TipoPeriodicidadePagamento.Semanal, label: 'Semana' },
    { valor: TipoPeriodicidadePagamento.Quinzenal, label: 'Quinzena' },
    { valor: TipoPeriodicidadePagamento.Mensal, label: 'Mês' },
];

export interface ApiConfig {
    endpoint: string;
    requisicaoBemSucedida: (response: any) => void;
    tratarErro: (error: AxiosError<ApiError>) => void;
    idTenentCampo: string;
}

export interface AcessoProps extends RouteComponentProps<Acesso> {
}

export interface ListagemProps extends RouteComponentProps<Listagem> {
}

export interface Dashboard {
    id: string;
    totalizadoresVeiculosDisponiveis: number;
    totalizadoresVeiculosEmLocacao: number;
    totalizadoresServicosRealizados: number;
    totalizadoresPecasEmEstoque: number;
    totalizadoresLocacoesAtivas: number;
    totalizadoresLocacoesEncerradas: number;
    totalizadoresLocatarios: number;
    totalizadoresPrestadoresDeServico: number;
    totalizadoresQuantidadeMotos: number;
    totalizadoresQuantidadeCarros: number;
    totalizadoresQuantidadePagamentosPagos: number;
    totalizadoresQuantidadePagamentosPendentes: number;
    totalizadoresQuantidadePagamentosAtraso: number;
    totalizadoresQuantidadeLancamentoEntrada: number;
    totalizadoresQuantidadeLancamentoSaida: number;
    totalizadoresSomaPagamentosPagos: number;
    totalizadoresSomaPagamentosPendentes: number;
    totalizadoresSomaPagamentosAtraso: number;
    totalizadoresSomaLancamentoEntradaResolvido: number;
    totalizadoresSomaLancamentoSaidaResolvido: number;
    totalizadoresSomaLancamentoEntradaPendente: number;
    totalizadoresSomaLancamentoEntradaVencidas: number;
    totalizadoresSomaLancamentoSaidaPendente: number;
    totalizadoresSomaLancamentoSaidaVencidas: number;
    totalizadoresSomaMultas: number;
    totalizadoresSomaMultasVencidas: number;
    totalizadoresSomaMultasAVencer: number;
    totalizadoresSomaMultasPagas: number;
    caixaEntrada: number;
    caixaSaida: number;
    gastoEmServicos: number;
    investidoEmVeiculo: number;
    investidoEmVeiculoMoto: number;
    investidoEmVeiculoCarro: number;
    faturamentoPorVeiculo: VeiculoData[];
    custoPorVeiculo: VeiculoData[];
    lucroPorVeiculo: VeiculoData[];
    totalizadoresSaidasResolvidas: number;
    totalizadoresVistorias: number;
    totalizadoresTrocasDeOleo: number;
    saldoEntradasSaidas: number;
    veiculosDisponiveisPorTipo?: DashboardVeiculoPorTipo[];
    veiculosEmLocacaoPorTipo?: DashboardVeiculoPorTipo[];
    investidoEmVeiculoPorTipo?: DashboardVeiculoPorTipo[];
}

export interface DashboardVeiculoPorTipo {
    tipoVeiculo: number;
    quantidade: number;
    valor: number;
}

export interface Auditoria {
    id: string;
    usuarioId: string | null;
    nomeUsuario: string;
    idEmpresa: string | null;
    entidade: string;
    acao: string;
    dadosAntigos: string | null;
    dadosNovos: string | null;
    chavePrimaria: string;
    ip: string;
    dataHora: string;
}

export enum StatusNFSe {
    Processando = 0,
    Emitida = 1,
    Cancelada = 2,
    Erro = 3,
    Agendada = 4
  }
  
  export interface NFService {
    id: string;
    chaveNota?: string;
    protocolo?: string;
    status: StatusNFSe;
    codigoVerificacao?: string;
    idEmpresa: string;
    idPagamento?: string;
    idLocatario?: string;
    idServico?: string;
    valor: number;
    dataHoraEmissao?: Date;
    competencia: Date;
    mensagemErro?: string;
    numeroNota?: string;
    linkNota?: string;
    xml?: string;
    nomeLocatario?: string;
    descricaoPagamento?: string;
  }
  
  export interface NFServiceFilter {
    idEmpresa: string;
    dataInicio?: Date;
    dataFim?: Date;
    status?: StatusNFSe;
    page: number;
    pageSize: number;
  }

  
export interface Permissao {
    id?: string;
    nome: string;
    grupo: string;
    chave: string;
}

export enum TpRelatorioGeral {
    Nenhum = 0,
    ServicoVeiculo = 1,
    Locacao = 2,
    Veiculo = 3,
}

export enum TpDesconto {
    Percentual = 1,
    ValorFixo = 2,
}

export enum TpRelatorioFinanceiro {
    Nenhum = 0,
    Despesas = 1,
    Receitas = 2,
    FluxoCaixa = 3,
    Comissoes = 4,
}

export enum TpPlano {
    Mensal = 'mensal',
    Anual = 'anual',
}

export enum TpEscolhaRelatorioVeiculosSituacao {
    Ativo = 1,
    Parado = 2,
    Todos = 3,
    Vendido = 4,
    Roubado = 5,
    Inativo = 6,
}

export type PlanType = 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL';
export type PaymentMethod = 'pix' | 'creditcard';

export interface PlanOption {
    id: PlanType;
    name: string;
    discount: number;
    cycleMonths: number;
    description: string;
    backendValue: string;
}

export const PLAN_OPTIONS: PlanOption[] = [
    {
        id: 'MONTHLY',
        name: 'Mensal',
        discount: 0,
        cycleMonths: 1,
        description: 'Flexibilidade total',
        backendValue: 'MONTHLY'
    },
    {
        id: 'QUARTERLY',
        name: 'Trimestral',
        discount: 0.05,
        cycleMonths: 3,
        description: '5% de desconto',
        backendValue: 'QUARTERLY'
    },
    {
        id: 'SEMIANNUAL',
        name: 'Semestral',
        discount: 0.15,
        cycleMonths: 6,
        description: '15% de desconto',
        backendValue: 'SEMIANNUALLY'
    },
    {
        id: 'ANNUAL',
        name: 'Anual',
        discount: 0.25,
        cycleMonths: 12,
        description: '25% de desconto',
        backendValue: 'ANNUAL'
    }
];

export function getAdjustedDiscount(plan: PlanType, parcelado: boolean): number {
    const option = PLAN_OPTIONS.find(p => p.id === plan);
    if (!option) return 0;
    if (!parcelado) return option.discount;
    if (plan === 'SEMIANNUAL' || plan === 'ANNUAL') {
        return option.discount - 0.05;
    }
    return option.discount;
}

// LocaSmartCheck Pricing
export const LOCASMARTCHECK_BASE_PRICE = 19.90;
export const LOCASMARTCHECK_INCLUDED_PLATES = 5;
export const LOCASMARTCHECK_EXTRA_PLATE_PRICE = 3.00;

export function calcularPrecoLocaSmartCheck(placas: number): number {
    const placasAdicionais = Math.max(0, placas - LOCASMARTCHECK_INCLUDED_PLATES);
    return LOCASMARTCHECK_BASE_PRICE + (placasAdicionais * LOCASMARTCHECK_EXTRA_PLATE_PRICE);
}

// LocaSmartCheck Integration types
export interface CheckInspectionTemplate {
    id: string;
    name: string;
    description: string;
    isActive: boolean;
    items: CheckInspectionTemplateItem[];
}

export interface CheckInspectionTemplateItem {
    id: string;
    name: string;
    description: string;
    order: number;
    requiresPhoto: boolean;
}

export interface CheckInspection {
    id: string;
    vehicleId: string;
    driverId: string;
    status: string;
    startedAt: string;
    completedAt: string;
    notes: string;
    mileage: number;
    createdAt: string;
    vehiclePlate: string;
    vehicleModel: string;
    driverName: string;
    inspectorName: string;
    templateName: string;
    items: CheckInspectionItem[];
}

export interface CheckInspectionItem {
    id: string;
    templateItemName: string;
    status: string;
    notes: string;
    photos: CheckInspectionPhoto[];
}

export interface CheckInspectionPhoto {
    id: string;
    fileName: string;
    filePath: string;
    contentType: string;
    fileSize: number;
}

export interface ProvisionStepLog {
    stepName: string;
    status: string;
    count: number;
    message: string;
    durationMs: number;
}

export interface ProvisionResult {
    success: boolean;
    tenantId: string;
    errorMessage: string;
    steps: ProvisionStepLog[];
}

export interface StatusIntegracao {
    integrado: boolean;
    tenantId: string;
    tenantStatus: string;
}

export function getMaxInstallments(totalValue: number): number {
    if (totalValue > 1500) return 5;
    if (totalValue > 1000) return 4;
    if (totalValue > 500) return 3;
    return 1;
}

export function getInstallmentOptions(totalValue: number): { count: number; label: string; value: number }[] {
    const options: { count: number; label: string; value: number }[] = [
        { count: 1, label: `1x de ${totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} (a vista)`, value: totalValue }
    ];
    const max = getMaxInstallments(totalValue);
    for (let i = 2; i <= max; i++) {
        const installmentValue = Math.round((totalValue / i) * 100) / 100;
        options.push({
            count: i,
            label: `${i}x de ${installmentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            value: installmentValue
        });
    }
    return options;
}

export function isInstallmentEligible(plan: PlanType): boolean {
    return plan === 'SEMIANNUAL' || plan === 'ANNUAL';
}

// ============================================================
// Bancos
// ============================================================

export enum TipoContaBancaria {
    Corrente = 1,
    Poupanca = 2,
    Pagamento = 3,
}

export enum TipoChavePix {
    CPF = 1,
    CNPJ = 2,
    Email = 3,
    Telefone = 4,
    Aleatoria = 5,
}

export interface Banco {
    id: string;
    idEmpresa: string;
    codigoFebraban?: string;
    nomeBanco: string;
    apelido: string;
    tipoConta: TipoContaBancaria;
    agencia: string;
    digitoAgencia?: string;
    conta: string;
    digitoConta?: string;
    titular?: string;
    documentoTitular?: string;
    tipoChavePix?: TipoChavePix;
    chavePix?: string;
    observacoes?: string;
    ativo: boolean;
    dataCadastro?: string;
    dataAtualizacao?: string;
}

export interface BancoFebraban {
    ispb?: number;
    name?: string;
    code?: number;
    fullName?: string;
}

export interface ConciliarBancoRequest {
    idBanco: string;
    aplicarATodos: boolean;
}

export interface ConciliarBancoResponse {
    pagamentosAtualizados: number;
    lancamentosAtualizados: number;
}

export enum TipoPendencia {
    Faltando = 0,
    Divergente = 1,
}

export interface LocatarioPendente {
    id: string;
    nome: string;
    documento: string;
    tipo: TipoPendencia;
    camposDivergentes: string[];
}

export interface VeiculoPendente {
    id: string;
    placa: string;
    marcaModelo: string;
    tipo: TipoPendencia;
    camposDivergentes: string[];
}

export interface VinculoPendente {
    veiculoId: string;
    veiculoPlaca: string;
    locatarioId: string;
    locatarioNome: string;
}

export interface AcessoAppPendente {
    locatarioId: string;
    locatarioNome: string;
    email: string;
}

export interface PendentesSincronizacao {
    empresaIntegrada: boolean;
    locatarios: LocatarioPendente[];
    veiculos: VeiculoPendente[];
    vinculos: VinculoPendente[];
    acessosApp: AcessoAppPendente[];
}

export interface VinculoIds {
    veiculoId: string;
    locatarioId: string;
}

export interface SincronizarRequest {
    locatariosIds: string[];
    veiculosIds: string[];
    vinculos: VinculoIds[];
    acessosAppLocatarios: string[];
}

export interface ResultadoSincronizacao {
    locatariosSincronizados: number;
    veiculosSincronizados: number;
    vinculosSincronizados: number;
    acessosAppSincronizados: number;
    erros: string[];
}

// ============================================================
// Gestão de Risco
// ============================================================

export interface EventoRiscoAlvo {
    idVeiculo?: string;
    idLocatario?: string;
    idLocacao?: string;
    pausado: boolean;
    pausadoAte?: string;
}

export interface EventoRisco {
    id?: string;
    idEmpresa: string;
    idFilial?: string;
    nome: string;
    tipo: number;            // 1=BloqueioInadimplencia
    ativo: boolean;
    pausadoGlobal: boolean;
    alvoTipo: number;        // 1=Todos,2=PorVeiculo,3=PorLocatario
    temporario: boolean;
    dataExpiracao?: string;
    alertarMotorista: boolean;
    alertarLocador: boolean;
    canaisMotorista: number; // flags: 1=Push,2=Email,4=InApp
    canaisLocador: number;
    configuracao: string;    // JSON
    execucoes?: number;
    alvos: EventoRiscoAlvo[];
}

export interface EventoRiscoLog {
    id: string;
    categoria: number;       // 1=Info,2=Notificacao,3=Acao,4=Erro
    tipo: number;
    idVeiculo?: string;
    idLocatario?: string;
    placa?: string;
    mensagem: string;
    detalhe?: string;
    dataHora: string;
    sucesso?: boolean;
}